/**
 * Shared constants and utility functions for Admin Dashboard tabs
 */

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const DATA_RETENTION_TEXT = "Delivery data is stored for 2 years";
export const ITEMS_PER_PAGE = 500;
export const IMAGES_PER_PAGE = 20;
export const POLL_INTERVAL_MS = 10000;

// Chart colors for financial visualizations
export const CHART_COLORS = ['#f97316', '#22c55e', '#3b82f6', '#eab308', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

/**
 * Get tomorrow's date in Singapore timezone (UTC+8)
 */
export const getTomorrowSGT = () => {
  const now = new Date();
  const sgtOffset = 8 * 60;
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const sgtTime = new Date(utc + (sgtOffset * 60000));
  sgtTime.setDate(sgtTime.getDate() + 1);
  return sgtTime.toISOString().split('T')[0];
};

/**
 * Get today's date in Singapore timezone (UTC+8) as YYYY-MM-DD.
 * Uses Intl.DateTimeFormat for reliable timezone conversion regardless of
 * the browser's local timezone.
 */
export const getTodaySGT = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' }).format(new Date());
};

/**
 * Check if a job is expired.
 * Jobs are valid only on their assigned_date (SGT). A job is expired the
 * moment SGT enters the next calendar day — jobs do not roll over.
 */
export const isJobExpired = (job) => {
  if (job.status === 'completed') return false;
  if (!job.assigned_date) return false;
  // String comparison works for ISO YYYY-MM-DD
  return job.assigned_date < getTodaySGT();
};

/**
 * Get status color for jobs
 */
export const getStatusColor = (status, expired = false) => {
  if (expired) return 'bg-red-500/20 text-red-400 border-red-500/30';
  switch (status) {
    case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'in_progress': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
};

/**
 * Format date for display (dd/mm/yyyy)
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Format date with time (dd/mm/yyyy HH:mm)
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Format currency
 */
export const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return `$${Number(value).toLocaleString()}`;
};
